import { FloatingFormItem } from '@/components/_common';
import InfiniteSelect from '@/components/_common/InfiniteSelect';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useCompaniesReduced } from '@/core/domain/enterprise/Companies/Companies.hooks';
import useCommonGenerateReport from '@/core/domain/reports/commons/Report.hook';
import { useDefaultFormErrorNotification, useReportGenerate } from '@/hooks';
import useFileLoading, { FileType } from '@/hooks/useFileLoading';
import { ReportCallback } from '@/hooks/useReportGenerate';
import { applyMask } from '@/utils/mask';
import { cnpjMask } from '@/utils/mask/commonMasks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { Button, Checkbox, Col, Form, Modal, Row } from 'antd';
import { DefaultOptionType } from 'antd/lib/select';

export type ReportTypeCompanySelect =
  | 'workOrder'
  | 'checklist'
  | 'mechanicsRecord'
  | 'receipt-template';

interface WorkorderCompanySelectModalProps {
  open: boolean;
  reportType?: ReportTypeCompanySelect;
  close: () => void;
}

interface SelectCompanyInterface {
  companyData: string;
  editable: boolean;
}

interface ParserInterface {
  companyId: number;
  companyCnpj: string;
  companyName: string;
}

export default function WorkorderCompanySelectModal({
  open,
  reportType,
  close,
}: WorkorderCompanySelectModalProps) {
  const [form] = Form.useForm<SelectCompanyInterface>();
  const { sendFormErrorNotification } = useDefaultFormErrorNotification();
  const { openFile, downloadFile } = useReportGenerate();
  const { fileLoading, setFileLoading, setFileUnloading, FILE_FORMAT } =
    useFileLoading();

  const { data, isLoading, hasNextPage, fetchNextPage } = useCompaniesReduced(
    { active: true, isCompanyFilter: true },
    open,
  );

  const companies = flatInfiniteData(data);

  const companyOptions: DefaultOptionType[] = companies.map((cur) => ({
    label: cur.name,
    value: JSON.stringify({
      companyId: cur.id,
      companyCnpj: cur.cnpj,
      companyName: cur.name,
    }),
  }));

  const mutation = useCommonGenerateReport(
    `/generate/template/${reportType}`,
    'Gerado com sucesso',
    reportType === 'workOrder'
      ? 'Modelo de orçamento gerado com sucesso!'
      : 'Modelo de checklist gerado com sucesso!',
  );

  const handleGenerateReport = async (
    format: FileType,
    callback: ReportCallback,
  ) => {
    setFileLoading(format);
    try {
      const formItens = await form.validateFields();
      const dataParser: ParserInterface = JSON.parse(formItens.companyData);
      const editable = formItens.editable;
      const obj =
        reportType === 'workOrder'
          ? {
              companyId: dataParser.companyId,
              format: FILE_FORMAT[format],
              editable,
            }
          : {
              companyName: dataParser.companyName,
              companyCnpj: applyMask(dataParser.companyCnpj, cnpjMask),
              format: FILE_FORMAT[format],
            };
      const result = await mutation.mutateAsync(obj);

      if (callback) {
        callback(result);
      }
      form.resetFields();
      close();
    } catch (err) {
      sendFormErrorNotification(err);
    }
    setFileUnloading(format);
  };

  const handleButtonClick = async () => {
    if (reportType) {
      const formItens = await form.validateFields();
      const dataParser: ParserInterface = JSON.parse(formItens.companyData);

      const linkMap: { [key: string]: string } = {
        mechanicsRecord: `/privateReports/mechanicsRecord/${dataParser.companyId}`,
        'receipt-template': `/privateReports/receiptRecord/${dataParser.companyId}`,
      };

      const linkRedirect = linkMap[reportType];

      if (linkRedirect) window.open(linkRedirect, '_blank');

      close();
    }
  };

  const isRedirect =
    reportType && ['mechanicsRecord', 'receipt-template'].includes(reportType);

  return (
    <Modal
      width={600}
      open={open}
      onCancel={() => {
        close();
        form.resetFields();
      }}
      title="Selecione a empresa"
      footer={
        <SpaceForButton justifyContent="end">
          <Button
            block
            onClick={() => {
              close();
              form.resetFields();
            }}
          >
            Cancelar
          </Button>

          {isRedirect && (
            <Button type="primary" onClick={handleButtonClick}>
              Visualizar
            </Button>
          )}

          {!isRedirect && (
            <>
              <Button
                block
                loading={fileLoading.PDF}
                type="primary"
                key="pdf"
                onClick={() =>
                  handleGenerateReport('PDF', (result) =>
                    downloadFile({
                      result,
                      fileName:
                        reportType === 'checklist'
                          ? 'checklist.pdf'
                          : 'modelo_de_orcamento.pdf',
                    }),
                  )
                }
              >
                Gerar PDF
              </Button>

              <Button
                block
                loading={fileLoading.REPORT_VIEW}
                type="primary"
                key="viewReport"
                onClick={() =>
                  handleGenerateReport('REPORT_VIEW', (result) =>
                    openFile({
                      result,
                      fileName:
                        reportType === 'checklist'
                          ? 'checklist.pdf'
                          : 'modelo_de_orcamento.pdf',
                      fileType: 'application/pdf',
                    }),
                  )
                }
              >
                Visualizar Relatório
              </Button>
            </>
          )}
        </SpaceForButton>
      }
    >
      <Form form={form}>
        <Row gutter={[16, 16]}>
          <Col span={24}>
            <FloatingFormItem
              name="companyData"
              label="Empresa"
              rules={[{ required: true }]}
            >
              <InfiniteSelect
                options={companyOptions}
                loading={isLoading}
                hasNextPage={hasNextPage}
                fetchNextPage={fetchNextPage}
              />
            </FloatingFormItem>
          </Col>

          <Col span={24}>
            <FloatingFormItem name="editable" valuePropName="checked">
              <Checkbox>Editar PDF</Checkbox>
            </FloatingFormItem>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
}
